/**
 * File Upload Module
 * Handles uploading files to Azure Blob Storage and sending them to the agent
 */

import { getUserEmail } from "./auth";
import { getConfig } from "./config";

export interface UploadedFile {
  fileName: string;
  contentType: string;
  size: number;
  url: string;
  blobName: string;
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

/**
 * Allowed file types for upload
 */
export const ALLOWED_FILE_TYPES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-powerpoint",
  "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  "text/plain",
  "text/csv",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
];

/**
 * Max file size in bytes (10MB)
 */
export const MAX_FILE_SIZE = 10 * 1024 * 1024;

/**
 * Get the API base URL from runtime config
 */
function getApiUrl(): string {
  try {
    const config = getConfig();
    return config.api?.baseUrl || "/api";
  } catch {
    return "/api";
  }
}

/**
 * Check if a file type is allowed
 */
export function isFileTypeAllowed(file: File): boolean {
  return ALLOWED_FILE_TYPES.includes(file.type);
}

/**
 * Check if a file size is within limits
 */
export function isFileSizeAllowed(file: File): boolean {
  return file.size <= MAX_FILE_SIZE;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
}

/**
 * Get file extension from filename
 */
export function getFileExtension(fileName: string): string {
  return fileName.split(".").pop()?.toLowerCase() || "";
}

/**
 * Get appropriate icon for file type
 */
export function getFileIcon(contentType: string): string {
  if (contentType.startsWith("image/")) return "🖼️";
  if (contentType === "application/pdf") return "📄";
  if (contentType.includes("word")) return "📝";
  if (contentType.includes("excel") || contentType.includes("spreadsheet"))
    return "📊";
  if (
    contentType.includes("powerpoint") ||
    contentType.includes("presentation")
  )
    return "📽️";
  if (contentType.startsWith("text/")) return "📃";
  return "📎";
}

/**
 * Upload a file to Azure Blob Storage
 */
export async function uploadFile(
  file: File,
  onProgress?: (progress: UploadProgress) => void,
): Promise<UploadedFile> {
  const userEmail = getUserEmail();

  if (!userEmail) {
    throw new Error("User not authenticated");
  }

  // Validate file
  if (!isFileTypeAllowed(file)) {
    throw new Error(`File type '${file.type}' is not allowed`);
  }

  if (!isFileSizeAllowed(file)) {
    throw new Error(
      `File size exceeds maximum of ${formatFileSize(MAX_FILE_SIZE)}`,
    );
  }

  const apiUrl = getApiUrl();
  const formData = new FormData();
  formData.append("file", file);

  // Use XMLHttpRequest for progress tracking
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener("progress", (event) => {
      if (event.lengthComputable && onProgress) {
        onProgress({
          loaded: event.loaded,
          total: event.total,
          percentage: Math.round((event.loaded / event.total) * 100),
        });
      }
    });

    xhr.addEventListener("load", () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const response = JSON.parse(xhr.responseText);
          resolve({
            fileName: response.fileName,
            contentType: response.contentType,
            size: response.size,
            url: response.url,
            blobName: response.blobName,
          });
        } catch (e) {
          reject(new Error("Invalid response from server"));
        }
      } else {
        try {
          const error = JSON.parse(xhr.responseText);
          reject(new Error(error.error || "Upload failed"));
        } catch {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      }
    });

    xhr.addEventListener("error", () => {
      reject(new Error("Network error during upload"));
    });

    xhr.addEventListener("abort", () => {
      reject(new Error("Upload aborted"));
    });

    xhr.open("POST", `${apiUrl}/upload`);
    xhr.withCredentials = true;
    xhr.setRequestHeader("X-User-Email", userEmail);
    xhr.send(formData);
  });
}

/**
 * Create a display message for the attachment (no URL - that goes in channelData)
 */
export function createAttachmentDisplayText(
  uploadedFile: UploadedFile,
): string {
  const icon = getFileIcon(uploadedFile.contentType);
  return `${icon} ${uploadedFile.fileName} (${formatFileSize(uploadedFile.size)})`;
}

/**
 * Create an attachment message for the agent (includes URL for backward compatibility)
 */
export function createAttachmentMessage(uploadedFile: UploadedFile): string {
  const icon = getFileIcon(uploadedFile.contentType);
  return `${icon} ${uploadedFile.fileName} (${formatFileSize(uploadedFile.size)})`;
}

/**
 * Create a Bot Framework Activity attachment object
 */
export function createActivityAttachment(uploadedFile: UploadedFile): any {
  return {
    contentType: uploadedFile.contentType,
    contentUrl: uploadedFile.url,
    name: uploadedFile.fileName,
  };
}
